import React, { useEffect, useState, useMemo } from "react";
import { Box, SxProps, Theme, useTheme } from "@mui/material";
import { UserAvatar } from "./user-avatar";
import { AvatarCircleData } from "../../../types/user";
import { useAvatarCircleStore } from "../../../stores/avatarCircleStore";
import { useStatisticsStore } from "../../../stores/statisticsStore";
import { useAppStore } from "../../../stores/appStore";

const levelColors = {
  gold: "#FFD700",
  silver: "#C0C0C0",
  bronze: "#CD7F32",
};

type AvatarWithCircleProps = {
  size: string;
  sx?: SxProps<Theme>;
  name?: string;
  img?: string;
  userId?: string;
  hideText?: boolean;
  showOnlyLevel?: boolean;
  animationTrigger?: number | string;
  useGlobalTrigger?: boolean;
  isCurrentUser?: boolean;
  // For calculating progress from leaderboard data
  score?: number;
  minScore?: number;
  maxScore?: number;
  place?: number;
  totalParticipants?: number;
};

export const AvatarWithCircle: React.FC<AvatarWithCircleProps> = ({
  size,
  sx,
  name,
  img,
  userId,
  hideText = false,
  showOnlyLevel = false,
  animationTrigger,
  useGlobalTrigger = false,
  isCurrentUser = true,
  score,
  minScore,
  maxScore,
  place,
  totalParticipants,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { fetchAvatarCircle, getCachedData } = useAvatarCircleStore();
  const { progressBar } = useStatisticsStore();
  const { avatarRingsAnimationTrigger } = useAppStore();
  const [circleData, setCircleData] = useState<AvatarCircleData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [animatedLevelProgress, setAnimatedLevelProgress] = useState(0);
  const [animatedWeeklyScoreProgress, setAnimatedWeeklyScoreProgress] =
    useState(0);
  const [animatedCompetencesProgress, setAnimatedCompetencesProgress] =
    useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      setCircleData(null);
      return;
    }

    const cached = getCachedData(userId);
    if (cached) {
      setCircleData(cached);
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setIsLoading(true);
        const data = await fetchAvatarCircle(userId);
        setCircleData(data);
      } catch (error) {
        console.error("Error fetching avatar circle data:", error);
        setCircleData({
          background_color: null,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [userId, fetchAvatarCircle, getCachedData]);

  const progressData = useMemo(() => {
    if (isCurrentUser) {
      if (!progressBar || progressBar.length === 0) return null;

      const answeredQuiz = progressBar.find(
        (item) => item.name === "Answered quiz",
      );
      const weeklyScore = progressBar.find(
        (item) => item.name === "Current week score",
      );
      const focusProgress = progressBar.find(
        (item) => item.name === "Progress of the focus",
      );

      return {
        level: answeredQuiz
          ? { value: answeredQuiz.value, total: answeredQuiz.total }
          : null,
        weekly_score: weeklyScore
          ? { value: weeklyScore.value, total: weeklyScore.total }
          : null,
        competences: focusProgress
          ? { value: focusProgress.value, total: focusProgress.total }
          : null,
      };
    }

    // For other users, first try to use data from circleData (API)
    if (circleData) {
      const hasProgressData =
        circleData.level !== undefined ||
        circleData.weekly_score !== undefined ||
        circleData.competences !== undefined;

      if (hasProgressData) {
        return {
          level:
            circleData.level !== undefined
              ? { value: circleData.level, total: circleData.level_max ?? 9 }
              : null,
          weekly_score:
            circleData.weekly_score !== undefined
              ? { value: circleData.weekly_score, total: 45 }
              : null,
          competences:
            circleData.competences !== undefined
              ? { value: circleData.competences, total: 5 }
              : null,
        };
      }
    }

    // If no API data, calculate progress from leaderboard score
    if (
      score !== undefined &&
      minScore !== undefined &&
      maxScore !== undefined
    ) {
      const scoreRange = maxScore - minScore;
      const progressRatio =
        scoreRange > 0 ? (score - minScore) / scoreRange : 0.5;

      const levelValue = Math.round(progressRatio * 9); // 0-9 for Answered quiz
      const weeklyScoreValue = Math.round(progressRatio * 45); // 0-45 for Weekly score
      const competencesValue = Math.max(0.1, progressRatio * 5); // 0-5 for Focus progress

      return {
        level: levelValue > 0 ? { value: levelValue, total: 9 } : null,
        weekly_score:
          weeklyScoreValue > 0 ? { value: weeklyScoreValue, total: 45 } : null,
        competences:
          competencesValue > 0
            ? { value: Math.round(competencesValue * 10) / 10, total: 5 }
            : null,
      };
    }

    return null;
  }, [isCurrentUser, progressBar, circleData, score, minScore, maxScore]);

  const showCircles = useMemo(() => {
    if (showOnlyLevel) {
      return progressData?.level !== null && progressData?.level !== undefined;
    }
    return (
      (progressData &&
        (progressData.level ||
          progressData.weekly_score ||
          progressData.competences)) ||
      (!!circleData && (!!circleData.color || !!circleData.background_color))
    );
  }, [progressData, circleData, showOnlyLevel]);

  const sizeNum = parseFloat(size);
  const strokeWidth = Math.max(4, Math.min(6, sizeNum * 0.08));
  const circleOffset = strokeWidth * 1.1;
  const baseRadius = sizeNum / 2 + strokeWidth * 0.7;
  const svgPadding = strokeWidth * 3;
  const svgSize = sizeNum + svgPadding * 2;

  const defaultColors = {
    weekly_score: "#32D74B",
    competences: "#00C7BE",
  };

  const levelProgress = useMemo(() => {
    if (!progressData?.level) return 0;
    const { value, total } = progressData.level;
    return total > 0 ? Math.min(100, (value / total) * 100) : 0;
  }, [progressData]);

  // Define level color based on progress
  const getLevelColor = useMemo(() => {
    if (!progressData?.level) return levelColors.gold;
    const progress = levelProgress;
    if (progress >= 67) return levelColors.gold; // Gold: 67-100%
    if (progress >= 34) return levelColors.silver; // Silver: 34-66%
    return levelColors.bronze; // Bronze: 0-33%
  }, [progressData, levelProgress]);

  const weeklyScoreProgress = useMemo(() => {
    if (!progressData?.weekly_score) {
      return 0;
    }
    const { value, total } = progressData.weekly_score;
    const progress = total > 0 ? Math.min(100, (value / total) * 100) : 0;
    return progress;
  }, [progressData]);

  const competencesProgress = useMemo(() => {
    if (!progressData?.competences) return 0;
    const { value, total } = progressData.competences;
    return total > 0 ? Math.min(100, (value / total) * 100) : 0;
  }, [progressData]);

  useEffect(() => {
    if (isLoading || !showCircles || hasAnimated || !progressData) return;

    const targetLevel = levelProgress;
    const targetWeeklyScore = weeklyScoreProgress;
    const targetCompetences = competencesProgress;

    if (
      targetLevel === 0 &&
      targetWeeklyScore === 0 &&
      targetCompetences === 0
    ) {
      setHasAnimated(true);
      return;
    }

    const animationDuration = 1500;
    const startTime = Date.now();
    const startLevel = 0;
    const startWeeklyScore = 0;
    const startCompetences = 0;

    const easeInOutCubic = (t: number): number => {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(1, elapsed / animationDuration);

      const easedProgress = easeInOutCubic(progress);

      setAnimatedLevelProgress(
        startLevel + (targetLevel - startLevel) * easedProgress,
      );
      setAnimatedWeeklyScoreProgress(
        startWeeklyScore +
          (targetWeeklyScore - startWeeklyScore) * easedProgress,
      );
      setAnimatedCompetencesProgress(
        startCompetences +
          (targetCompetences - startCompetences) * easedProgress,
      );

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setAnimatedLevelProgress(targetLevel);
        setAnimatedWeeklyScoreProgress(targetWeeklyScore);
        setAnimatedCompetencesProgress(targetCompetences);
        setHasAnimated(true);
      }
    };

    const timeoutId = setTimeout(() => {
      requestAnimationFrame(animate);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    isLoading,
    showCircles,
    progressData,
    levelProgress,
    weeklyScoreProgress,
    competencesProgress,
    hasAnimated,
  ]);

  const effectiveTrigger =
    useGlobalTrigger && animationTrigger !== undefined
      ? `${avatarRingsAnimationTrigger}-${animationTrigger}`
      : useGlobalTrigger
        ? avatarRingsAnimationTrigger
        : animationTrigger;

  useEffect(() => {
    if (effectiveTrigger !== undefined) {
      setHasAnimated(false);
      setAnimatedLevelProgress(0);
      setAnimatedWeeklyScoreProgress(0);
      setAnimatedCompetencesProgress(0);
    }
  }, [effectiveTrigger, avatarRingsAnimationTrigger, animationTrigger]);

  useEffect(() => {
    if (progressData && !hasAnimated) {
      setAnimatedLevelProgress(0);
      setAnimatedWeeklyScoreProgress(0);
      setAnimatedCompetencesProgress(0);
    }
  }, [
    progressData,
    progressData?.level?.value,
    progressData?.level?.total,
    progressData?.weekly_score?.value,
    progressData?.weekly_score?.total,
    progressData?.competences?.value,
    progressData?.competences?.total,
    hasAnimated,
  ]);

  const renderActivityRing = (
    progress: number,
    color: string,
    zIndex: number,
    circleIndex: number,
  ) => {
    const radius = baseRadius + circleIndex * circleOffset;
    const centerX = svgSize / 2;
    const centerY = svgSize / 2;
    const normalizedProgress = Math.min(100, Math.max(0, progress));

    const circumference = 2 * Math.PI * radius;

    const dashArray = circumference;
    const dashOffset =
      circumference - (normalizedProgress / 100) * circumference;

    return (
      <svg
        width={svgSize}
        height={svgSize}
        style={{
          position: "absolute",
          top: -svgPadding,
          left: -svgPadding,
          zIndex,
          pointerEvents: "none",
          overflow: "visible",
        }}
        viewBox={`0 0 ${svgSize} ${svgSize}`}
      >
        {/* Background circle (full ring, subtle) - always visible */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke={isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.2)"}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity={0.4}
        />
        {/* Progress ring - fills gradually */}
        {normalizedProgress > 0 && (
          <circle
            cx={centerX}
            cy={centerY}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={dashArray}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${centerX} ${centerY})`}
          />
        )}
      </svg>
    );
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "4px",
        ...sx,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!isLoading && showCircles && (
          <>
            {progressData ? (
              <>
                {showOnlyLevel ? (
                  progressData?.level !== null &&
                  renderActivityRing(animatedLevelProgress, getLevelColor, 1, 0)
                ) : (
                  <>
                    {/* Outer ring - Level (Answered quiz) - Gold/Silver/Bronze (circleIndex: 2) */}
                    {true &&
                      progressData?.level !== null &&
                      renderActivityRing(
                        animatedLevelProgress,
                        getLevelColor,
                        1,
                        2,
                      )}
                    {/* Middle ring - Weekly Score (Current week score, out of 45) (circleIndex: 1) */}
                    {true &&
                      progressData?.weekly_score !== null &&
                      renderActivityRing(
                        animatedWeeklyScoreProgress,
                        defaultColors.weekly_score,
                        2,
                        1,
                      )}
                    {/* Inner ring - Competences (Progress of the focus, out of 5) (circleIndex: 0) */}
                    {true &&
                      progressData?.competences !== null &&
                      renderActivityRing(
                        animatedCompetencesProgress,
                        defaultColors.competences,
                        3,
                        0,
                      )}
                  </>
                )}
              </>
            ) : (
              /* Legacy support for old circleData format */
              circleData && (
                <>
                  {circleData.background_color &&
                    renderActivityRing(100, circleData.background_color, 1, 0)}
                  {circleData.color &&
                    renderActivityRing(
                      Math.min(
                        100,
                        (Math.min(360, circleData.degrees || 0) / 360) * 100,
                      ),
                      circleData.color,
                      2,
                      0,
                    )}
                </>
              )
            )}
          </>
        )}
        <Box
          sx={{
            position: "relative",
            zIndex: 10,
          }}
        >
          <UserAvatar size={size} name={name} img={img} sx={sx} />
        </Box>
      </Box>
      {!hideText && !isLoading && circleData?.text && (
        <Box
          sx={{
            fontSize: "10px",
            m: 2,
            color: circleData.text_color || "text.secondary",
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          {circleData.text}
        </Box>
      )}
    </Box>
  );
};
